import React, { useEffect, useMemo, useState } from 'react';
import { eventAPI } from '../../lib/eventApi';
import { MagnifyingGlass } from '@phosphor-icons/react';

// SharedPartnerPicker — small searchable dropdown of approved members
// eligible to be selected as a shared partner for the given session.
//
// Excludes (server-side):
//   • the caller themselves
//   • members already ACTIVE players in this session
//   • members already attached as a shared partner of another reg
//
// Use as a controlled component: `value` is the selected profile_id (or
// null), `onChange(profileId)` fires when the user picks one. Calls
// `onError` on load failure.
export default function SharedPartnerPicker({ sessionId, value, onChange, disabled, testidPrefix = 'shared-picker' }) {
  const [eligible, setEligible] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    eventAPI.listEligibleSharedPartners(sessionId)
      .then((r) => { if (!cancelled) { setEligible(r.data?.eligible || []); setErrMsg(null); } })
      .catch((e) => { if (!cancelled) setErrMsg(e?.response?.data?.detail || 'Could not load members'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  const selected = useMemo(
    () => eligible.find((c) => c.profile_id === value) || null,
    [eligible, value]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return eligible;
    const q = query.toLowerCase();
    return eligible.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [eligible, query]);

  return (
    <div className="relative" data-testid={`${testidPrefix}-wrap`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled || loading}
        data-testid={`${testidPrefix}-toggle`}
        className="w-full text-left text-sm px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-[#013523] focus:outline-none focus:border-[#015e3f] flex items-center justify-between gap-2 disabled:opacity-60"
      >
        <span className="truncate">
          {loading
            ? 'Loading members...'
            : selected
              ? selected.name
              : <span className="text-[#a8a8a8]">Select a club member...</span>}
        </span>
        <MagnifyingGlass size={14} className="text-[#6a766e] shrink-0" />
      </button>
      {errMsg && (
        <div className="mt-1 text-[11px] text-red-600" data-testid={`${testidPrefix}-error`}>{errMsg}</div>
      )}
      {open && !disabled && (
        <div
          className="absolute z-20 mt-1 w-full bg-white border border-[#e3dcd1] rounded-lg shadow-lg max-h-64 overflow-auto"
          data-testid={`${testidPrefix}-dropdown`}
        >
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name..."
            className="w-full text-sm px-3 py-2 border-b border-[#e3dcd1] bg-[#fbfaf8] focus:outline-none"
            data-testid={`${testidPrefix}-search`}
          />
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[#6a766e]" data-testid={`${testidPrefix}-empty`}>
              No eligible members found.
            </div>
          ) : (
            <ul>
              {filtered.map((c) => (
                <li key={c.profile_id}>
                  <button
                    type="button"
                    onClick={() => { onChange(c.profile_id); setOpen(false); setQuery(''); }}
                    className={`w-full text-left text-sm px-3 py-2 hover:bg-[#015e3f]/5 ${
                      value === c.profile_id ? 'bg-[#015e3f]/10 text-[#015e3f]' : 'text-[#013523]'
                    }`}
                    data-testid={`${testidPrefix}-option-${c.profile_id}`}
                  >
                    {c.name}
                    {c.rating && <span className="ml-2 text-[10px] text-[#6a766e]">{c.rating}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
